import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

export type TLayer = 1 | 2 | 3;

type TModalProps = [
  component: ReactNode,
  opts?: { id?: string; layer?: TLayer },
];

export interface IOpenModal {
  (...args: [...TModalProps, replace?: boolean]): void;
}

export interface IReplaceModal {
  (...args: TModalProps): void;
}

export type TModalContext = {
  closeModal: () => void;
  currents: IModal[];
  openModal: IOpenModal;
  replaceModal: IReplaceModal;
};

export const ModalContext = createContext<TModalContext>({
  currents: [],
  openModal: () => {},
  replaceModal: () => {},
  closeModal: () => {},
});

export interface IModal {
  component: ReactNode;
  id?: string;
  layer: TLayer;
}

const defaultLayer: TLayer = 2;

export const ModalProvider: FC<PropsWithChildren> = ({ children } = {}) => {
  const [queue, setQueue] = useState<IModal[]>([]);

  const openModal = useCallback<IOpenModal>(
    (component, opts = {}, replace = false) => {
      const { id, layer = defaultLayer } = opts;
      setQueue((queue) => [
        { component, layer, id },
        ...(replace ? queue.slice(1, queue.length) : queue),
      ]);
    },
    [],
  );

  const replaceModal = useCallback<IReplaceModal>(
    (component, opts = {}) => {
      openModal(component, opts, true);
    },
    [openModal],
  );

  const closeModal = useCallback(() => {
    setQueue((queue) => queue.slice(1, queue.length));
  }, []);

  const currents = useMemo(
    () =>
      queue
        // Gets by one modal for each layer
        .reduce<IModal[]>(
          (acc, modal) =>
            acc.find((accModal) => accModal?.layer === modal.layer)
              ? acc
              : [...acc, modal],
          [],
        )
        .sort((a, b) => a.layer - b.layer),
    [queue],
  );

  const contextValue = useMemo(
    () => ({
      currents,
      openModal,
      replaceModal,
      closeModal,
    }),
    [currents, openModal, replaceModal, closeModal],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
};
